import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../lib/i18n";

/**
 * Compact corner dropdown showing the current language in its native name.
 * Click opens a list of every supported language. Pick is persisted via
 * i18next's localStorage detector ("binogi-buzz:lng").
 */
export function LanguageSwitcher({
  align = "right",
  available,
}: {
  align?: "left" | "right";
  // Codes the current quiz is renderable in. When supplied with 2+
  // entries the dropdown filters to those (since switching to a
  // language the quiz isn't translated into would just show EN
  // fallback for the content). When the quiz is single-language or
  // unknown, we still show the full list — picking a different
  // locale at least translates the UI chrome ("Get ready", "Top 5",
  // etc.) even though the question text stays in its source language.
  // Codes not in SUPPORTED_LANGUAGES are silently dropped.
  available?: string[];
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const languages =
    available && available.length > 1
      ? SUPPORTED_LANGUAGES.filter((l) => available.includes(l.code))
      : SUPPORTED_LANGUAGES;
  // Match i18n.language exactly to whatever pickLocalized() uses in
  // the renderers. If we used resolvedLanguage instead, a tab whose
  // i18n.language has fallen off the available set would label
  // itself with the wrong locale while the renderer happily picks
  // the requested one. Both must agree.
  // Browsers/devices report region-qualified locales (sv-SE, en-US).
  // Our SUPPORTED_LANGUAGES uses bare primary subtags (sv, en), so
  // fall back to the primary if the full code doesn't match exactly.
  const activeLang = i18n.language;
  const primary = activeLang?.split("-")[0];
  const current =
    languages.find((l) => l.code === activeLang) ??
    languages.find((l) => l.code === primary) ??
    languages.find((l) => l.code === "en") ??
    languages[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!current) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-white/80 hover:bg-brand-ink2 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("home.language_label")}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" />
          <path d="M1.5 7h11M7 1.5c2 2 2 9 0 11M7 1.5c-2 2-2 9 0 11" stroke="currentColor" />
        </svg>
        <span>{current.native}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={open ? "rotate-180" : ""}
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2 4l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <ul
          role="listbox"
          className={`absolute z-30 top-full mt-2 w-48 bg-brand-ink2 rounded-xl shadow-2xl ring-1 ring-white/10 py-1 max-h-80 overflow-y-auto ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {languages.map((l) => {
            const selected = l.code === current.code;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    void i18n.changeLanguage(l.code);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-brand-ink3 flex items-center gap-2 ${
                    selected ? "text-brand-green font-semibold" : "text-white"
                  }`}
                >
                  <span className="flex-1">{l.native}</span>
                  {selected && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path
                        d="M2 7l3.5 3.5L12 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
